// The catalog answer's Markdown, as the editor loads it into draft-js and saves it back. Kept out of Editor.tsx so the
// round trip can be tested on its own - a fault here shows only after a save and a reopen, as a change nobody made.

import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import { EDITOR_ONLY_LINE, decodeBlankLines, encodeBlankLines } from './blankLines';
import { mergeCodeLines } from './codeBlocks';
import { catalogImageId, formatImageTitle, imageSizeToken, imageWidthCss, parseImageTitle, serializedImageSrc } from './catalogImages';
import { escapeTypedListMarkers } from './listMarkers';
import { markdownLinkUrls } from './markdownLinks';

// react-draft-wysiwyg draws a picture with no alignment centred. Everywhere else the answer is shown -
// the portal, the Teams card, email - it sits on the left, so the editor says so too.
export const DEFAULT_IMAGE_ALIGNMENT = 'left';

const ImageRegexp = /^!\[([^\]]*)]\s*\(([^)"]+)( "([^)"]+)")?\)/;
const imageBlock = remarkable => {
  remarkable.block.ruler.before(
    'paragraph',
    'image',
    (state, startLine, endLine, silent) => {
      const pos = state.bMarks[startLine] + state.tShift[startLine];
      const max = state.eMarks[startLine];
      if (pos >= max) {
        return false;
      }
      if (!state.src) {
        return false;
      }
      if (state.src[pos] !== '!') {
        return false;
      }

      const match = ImageRegexp.exec(state.src.slice(pos));
      if (!match) {
        return false;
      }

      // in silent mode it shouldn't output any tokens or modify pending
      if (!silent) {
        state.tokens.push({
          type: 'image_open',
          src: match[2],
          alt: match[1],
          lines: [startLine, state.line],
          level: state.level,
          text: match[0]
        });

        state.tokens.push({
          text: match[0],
          type: 'inline',
          content: match[4] || '',
          level: state.level + 1,
          lines: [startLine, state.line],
          children: []
        });

        state.tokens.push({
          text: match[0],
          type: 'image_close',
          level: state.level
        });
      }

      state.line = startLine + 1;

      return true;
    }
  );
};

// export const convertMdToDraftST = md => mdToDraftjs()

export const convertMdToDraft = md =>
  markdownToDraft(md, {
    remarkablePlugins: [imageBlock],
    blockTypes: {
      image_open: item => {
        return {
          type: 'atomic',
          data: {
            src: item.src,
            alt: item.alt
          }
        };
      }
    }
  });

export interface StyleRange {
  offset: number;
  length: number;
  style: string;
}

export interface EntityRange {
  key?: string | number;
  length: number;
  offset: number;
}

/** What a picture entity carries in the catalog editor. */
export interface PictureData {
  src?: string;
  /** The stored `catalog-image:` address, kept while `src` shows a fetched copy. */
  marker?: string;
  alt?: string;
  alignment?: string;
  width?: string;
  height?: string;
  size?: string;
  /** Set when a stored picture could not be fetched, so it draws as unavailable. Never saved. */
  broken?: boolean;
}

export interface RawEntity {
  type: string;
  mutability?: string;
  data?: PictureData;
}

export interface Block {
  data?: {
    src?: string;
    [key: string]: unknown;
  };
  depth: number;
  entityRanges: EntityRange[];
  inlineStyleRanges: StyleRange[];
  key?: string;
  text: string;
  type: string;
}

export interface RawData {
  blocks: Block[];
  entityMap: Record<string, RawEntity>;
}

/** A stored answer as draft-js raw content: links put back, pictures as atomic blocks, and a line after a picture where one is needed. */
export const answerToRaw = (markdownString: string, keepBlankLines: boolean): RawData => {
  const linkUrls = markdownLinkUrls(markdownString);
  const fromDBRawData = convertMdToDraft(markdownString);
  if (keepBlankLines) fromDBRawData.blocks = decodeBlankLines(fromDBRawData.blocks);

  let newRawData = { blocks: [], entityMap: {} };
  let current_key = 0;
  let current_link = 0;
  let newEntityMap = {};
  let imagesIndex = [];
  newRawData.blocks = fromDBRawData.blocks.map((block: Block, index) => {
    let isBlockChanged = false;
    let newBlockFormat = {};
    if (block.entityRanges?.length > 0) {
      let newEntityRanges = [];
      block.entityRanges.forEach(er => {
        if (linkUrls[current_link]) {
          newEntityMap[current_key.toString()] = {
            type: 'LINK',
            mutability: 'MUTABLE',
            data: {
              url: linkUrls[current_link],
              targetOption: '_self'
            }
          };
          newEntityRanges.push({
            offset: er.offset,
            length: er.length,
            key: current_key
          });
          current_link++;
          current_key++;
        }
      });
      isBlockChanged = true;
      newBlockFormat = { ...block, entityRanges: newEntityRanges };
    }
    if (block.data && block.type === 'atomic') {
      if (block.data.src) {
        // Alignment rides in through the markdown image title slot
        // (`![alt](src "left")`) - markdown-draft-js surfaces that title
        // as the atomic block's plain text, so it has to be read here
        // before the next line clears it.
        const layout = parseImageTitle(block.text);
        const { alignment } = layout;
        const width = imageWidthCss(layout);
        block.text = ' ';
        block.entityRanges = [
          {
            key: current_key,
            length: 1,
            offset: 0
          }
        ];
        newEntityMap[current_key.toString()] = {
          type: 'IMAGE',
          mutability: 'IMMUTABLE',
          data: {
            url: block.data.src,
            src: block.data.src,
            fileName: '',
            ...(catalogImageId(block.data.src) ? { marker: block.data.src } : {}),
            alignment: alignment === 'center' ? 'none' : alignment || DEFAULT_IMAGE_ALIGNMENT,
            ...(width ? { width } : {}),
            ...(layout.naturalWidth ? { size: imageSizeToken(layout.naturalWidth, layout.naturalHeight) } : {})
          }
        };
        imagesIndex.push(index);
        delete block.data;
        current_key++;
      }
    } else if (block.type === 'code-block') block.type = 'code';

    return isBlockChanged ? newBlockFormat : block;
  });
  newRawData.entityMap = newEntityMap;
  // A picture needs a line after it only where there is nothing else to put the cursor on: when it is last, or
  // another picture follows. Adding one after every picture showed a gap the answer did not have - it was never
  // saved - so the editor looked spaced out where Teams, email and the portal were not. These lines are marked,
  // so saving drops them while they are still empty (see encodeBlankLines).
  const editorOnlyLine = () => ({ "depth": 0, "type": "unstyled", "text": "", "entityRanges": [], "inlineStyleRanges": [], "data": { [EDITOR_ONLY_LINE]: true } });
  const loadedBlocks = [...newRawData.blocks];
  const needsLineAfter = (index: number) => {
    const next = loadedBlocks[index + 1];
    return !next || next.type === 'atomic';
  };
  let i = 0;
  if(imagesIndex[0] === 0) {
    newRawData.blocks.unshift(editorOnlyLine());
    i++;
  }
  imagesIndex.forEach(index => {
    if (!needsLineAfter(index)) return;
    i++;
    newRawData.blocks.splice(index + i, 0, editorOnlyLine());
  })
  return newRawData;
};

/** The editor's raw content as the Markdown that is saved. */
export const answerToMarkdown = (rawdata: RawData, keepBlankLines: boolean): string => {
  // A line the editor added beside a picture, still empty, was never part of the answer. Left in, it went out as extra
  // newlines - invisible, but every open and save of an answer with two pictures together rewrote it.
  rawdata.blocks = rawdata.blocks.filter(b => !(b.data?.[EDITOR_ONLY_LINE] && b.text === ''));
  if (keepBlankLines) rawdata.blocks = encodeBlankLines(rawdata.blocks);
  rawdata.blocks = escapeTypedListMarkers(rawdata.blocks);
  const blocks = rawdata.blocks.map((r, idx) => {
    if (r.type === 'code') r.type = 'code-block';
    else if (r.type === 'unstyled' && idx !== rawdata.blocks.length - 1)
      r.text += '\n';
    return r;
  });

  rawdata.blocks = mergeCodeLines(blocks.filter(r => !(r.type === 'unstyled' && r.entityRanges.length > 0 && r.entityRanges.some(x => rawdata.entityMap[x.key].type === 'IMAGE'))));
  
  const draftOptions = {
    entityItems: {
      IMAGE: {
        open: entity => ' ',
        close: entity => {
          const { alt, alignment, width, size } = entity['data'];
          const src = serializedImageSrc(entity['data']);
          if (!src) return '';
          const formatted = formatImageTitle(alignment, width, size);
          const title = formatted ? ` "${formatted}"` : '';
          return `![${alt || ''}](${src}${title})`;
        }
      }
    },
    preserveNewlines: false
  };

  if (rawdata.blocks.length > 0 && (rawdata.blocks[0].text === "\n" || rawdata.blocks[0].text === "") && rawdata.blocks[0].type === "unstyled") {
    rawdata.blocks.shift();
  }
  return draftToMarkdown(rawdata, draftOptions);
};
